<script lang="ts" setup>
//#region Imports

import { computed, onMounted, ref } from 'vue';
import { required, email, minLength } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';

// Model
import type { VistoriaEtapaDadosPessoais } from '@/model/vistoria-etapas.interface';

// Service
import { saveDadosPessoais } from '@/service/vistoria-etapas/dados-pessoais';
import { useStepVistoria } from '@/stores/stepVistoria';

// Component
import IconCheck from '@/shared/icons/IconCheck.vue';
import Loading from '@/shared/component/show-loading.vue';
import SensitiveInput from '@/shared/component/SensitiveInput.vue';

// Other
import { PESSOA_FISICA, PESSOA_JURIDICA } from '@/global';
import { validateDate, validateCPF } from '@/shared/validate';
import { isSuppressedValue } from '@/shared/utils/sensitiveData';

//#endregion

const vistoriaStore = useStepVistoria();
const loading = ref<boolean>(true);
const nomeAssociado = vistoriaStore.vistoria!.nome;

const state = ref<VistoriaEtapaDadosPessoais>({
  idProspect: 0,
  email: '',
  telefone: null,
  celular: '',
  tipo: 'Pessoa Física',
  cpfcnpj: '',
  rgIe: '',
  validatedForm: false,
});

const validateEmailOrSuppressed = (value: string) =>
  isSuppressedValue(value) || email.$validator(value, null, null);

const validationRules = computed(() => {
  let localRules = {
    email: { required, email: validateEmailOrSuppressed },
    celular: { required, minLength: minLength(15) },
    rgIe: { required },
  };

  if (state.value.tipo === PESSOA_FISICA) {
    const validatePessoaFisica = {
      dataNascimento: {
        required,
        validateDate,
      },
    };

    localRules = Object.assign(localRules, validatePessoaFisica);
  }

  if (state.value.tipo === PESSOA_JURIDICA) {
    const validatePessoaFisica = {
      representanteLegal: {
        required,
        minLength: minLength(3),
      },
      cpfRepresentante: {
        required,
        validateCPF,
      },
    };

    localRules = Object.assign(localRules, validatePessoaFisica);
  }

  return localRules;
});

const v$ = useVuelidate(validationRules, state, { $autoDirty: true });

/**
 * Sanitiza os dados conforme parâmetro 'tipo'
 */
const sanitize = () => {
  if (state.value.tipo === PESSOA_FISICA) {
    state.value.representanteLegal = null;
    state.value.cpfRepresentante = null;
  }

  if (state.value.tipo === PESSOA_JURIDICA) {
    state.value.dataNascimento = null;
  }
};

/**
 * Avança para o próximo etapa
 */
const nextStep = async () => {
  const chave = vistoriaStore!.vistoria!.chave;
  const idEtapa = vistoriaStore!.currentVistoriaEtapa!.idEtapa;
  const dados: VistoriaEtapaDadosPessoais = Object.assign({}, state.value);

  const validForm = await v$.value.$validate();

  if (validForm) {
    dados.validatedForm = true;
    await saveDadosPessoais(chave, idEtapa, dados);
    await vistoriaStore.nextVistoriaEtapa();
  }
};

/**
 * Recupera os dados preenchidos anteriormente
 */
const getPreviousData = () => {
  const dadosPessoais = vistoriaStore.currentVistoriaEtapa?.dadosPessoais;

  if (dadosPessoais) {
    state.value = Object.assign({}, dadosPessoais);
  }

  state.value.idProspect = vistoriaStore.vistoria!.idProspect;
  sanitize();
};

onMounted(async () => {
  getPreviousData();

  // Tempo para atualizar front
  setTimeout(() => {
    loading.value = false;
  }, 300);
});
</script>

<template>
  <Loading :show="loading" />

  <div class="container pessoais__container">
    <span class="title">Dados Pessoais</span>

    <span class="texto">Preencha com os dados pessoais</span>

    <div class="pessoais__container__form">
      <!-- Nome -->
      <div class="form-group">
        <label class="form-label label-sm">Nome:*</label>
        <input class="form-input" type="text" disabled :value="nomeAssociado" />
      </div>

      <!-- CPF / CNPJ -->
      <div class="form-group">
        <label class="form-label label-sm"
          >{{ state.tipo === PESSOA_JURIDICA ? 'CNPJ' : 'CPF' }}*</label
        >
        <input
          class="form-input"
          type="text"
          disabled
          v-model="state.cpfcnpj"
        />
      </div>

      <!-- E-mail -->
      <div
        class="form-group"
        :class="{ 'has-error': v$.email?.$errors.length }"
      >
        <label class="form-label label-sm" for="input-email">E-mail:*</label>
        <SensitiveInput
          type="email"
          id="input-email"
          placeholder="Informe o seu e-mail"
          v-model="state.email"
        />
        <p class="form-input-hint">E-mail inválido</p>
      </div>

      <!-- Telefone -->
      <div class="form-group">
        <label class="form-label label-sm" for="input-telefone">Telefone</label>
        <SensitiveInput
          type="tel"
          id="input-telefone"
          placeholder="Informe o número do seu telefone"
          :mask="['(##) ####-####', '(##) #####-####']"
          v-model="state.telefone"
        />
      </div>

      <!-- Celular -->
      <div
        class="form-group"
        :class="{ 'has-error': v$.celular?.$errors.length }"
      >
        <label class="form-label label-sm" for="input-celular">Celular:*</label>
        <SensitiveInput
          type="tel"
          id="input-celular"
          placeholder="Informe o número do seu celular"
          mask="(##) #####-####"
          v-model="state.celular"
        />
        <p class="form-input-hint">Celular inválido</p>
      </div>

      <!-- Pessoa Física -->
      <div v-if="state.tipo === PESSOA_FISICA">
        <!-- Data de Nascimento -->
        <div
          class="form-group"
          :class="{ 'has-error': v$.dataNascimento?.$errors.length }"
        >
          <label class="form-label label-sm" for="input-datanascimento"
            >Data de Nascimento*</label
          >
          <SensitiveInput
            type="tel"
            id="input-datanascimento"
            placeholder="Informa a data de nascimento"
            mask="##/##/####"
            v-model="state.dataNascimento"
          />
          <p class="form-input-hint">Data nascimento inválida</p>
        </div>

        <!-- RG -->
        <div class="form-group">
          <label class="form-label label-sm" for="input-rg">RG:*</label>
          <SensitiveInput
            type="text"
            id="input-rg"
            placeholder="Informe o número do seu RG"
            v-model="state.rgIe"
          />
        </div>
      </div>

      <!-- Pessoa Jurídica -->
      <div v-if="state.tipo === PESSOA_JURIDICA">
        <!-- IE -->
        <div class="form-group">
          <label class="form-label label-sm" for="input-rg"
            >Inscrição Estadual:*</label
          >
          <SensitiveInput
            type="tel"
            id="input-rg"
            placeholder="Informe o número da IE"
            v-model="state.rgIe"
          />
        </div>

        <!-- Representante Legal -->
        <div
          class="form-group"
          :class="{ 'has-error': v$.representanteLegal?.$errors.length }"
        >
          <label class="form-label label-sm" for="input-representanteLegal"
            >Nome do Representante Legal:*</label
          >
          <SensitiveInput
            type="text"
            id="input-representanteLegal"
            placeholder="Informa o representante legal"
            v-model="state.representanteLegal"
          />
          <p class="form-input-hint">Representante Legal inválido</p>
        </div>

        <!-- CPF Representante Legal -->
        <div
          class="form-group"
          :class="{ 'has-error': v$.cpfRepresentante?.$errors.length }"
        >
          <label class="form-label label-sm" for="input-cpfRepresentante"
            >CPF do Representante Legal:*</label
          >
          <SensitiveInput
            type="tel"
            id="input-cpfRepresentante"
            placeholder="Informa o CPF do representante legal"
            mask="###.###.###-##"
            v-model="state.cpfRepresentante"
          />
          <p class="form-input-hint">CPF inválido</p>
        </div>
      </div>
    </div>

    <div class="btn-vistoria container__next">
      <button
        class="btn btn-primary"
        @click="nextStep()"
        :disabled="v$.$invalid"
      >
        <IconCheck />
        <span>Salvar Dados</span>
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.pessoais__container {
  margin: 0.8rem auto;

  span {
    display: block;
  }

  &__form {
    margin-top: 0.8rem;
    margin-bottom: 1.6rem;

    .form-input-hint {
      display: none;
    }

    .has-error {
      .form-input-hint {
        display: block;
      }
      p.form-input-hint {
        margin-bottom: 0;
      }
    }

    .divider {
      margin-top: 1.2rem;
    }
  }
}
</style>
